import { useEffect, useCallback, useRef } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  articlePublishedTime?: string;
  articleModifiedTime?: string;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  jsonLd,
  articlePublishedTime,
  articleModifiedTime,
}: SEOHeadProps) {
  const jsonLdIds = useRef<string[]>([]);

  const setMetaTag = useCallback((name: string, content: string, isProperty = false) => {
    const attr = isProperty ? 'property' : 'name';
    let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  }, []);

  useEffect(() => {
    const baseTitle = '樱花动漫 - 专注手机动漫的门户网站';
    const baseDescription = '如果您是手机动漫迷，樱花动漫网提供全面的动漫大全资源，这个门户网站专为移动端优化，让您轻松浏览和观看各种动漫，随时随地满足您的娱乐需求。';
    const baseKeywords = '樱花动漫,手机动漫门户,动漫大全,移动动漫资源';

    if (title) {
      document.title = `${title} - 樱花动漫`;
    } else {
      document.title = baseTitle;
    }

    setMetaTag('description', description || baseDescription);
    setMetaTag('keywords', keywords || baseKeywords);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    setMetaTag('og:title', ogTitle || title || baseTitle, true);
    setMetaTag('og:description', ogDescription || description || baseDescription, true);
    setMetaTag('og:type', ogType, true);
    if (canonicalUrl) {
      setMetaTag('og:url', canonicalUrl, true);
    }
    if (ogImage) {
      setMetaTag('og:image', ogImage, true);
    }

    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', ogTitle || title || baseTitle);
    setMetaTag('twitter:description', ogDescription || description || baseDescription);

    if (articlePublishedTime) {
      setMetaTag('article:published_time', articlePublishedTime);
    }
    if (articleModifiedTime) {
      setMetaTag('article:modified_time', articleModifiedTime);
    }

    // JSON-LD scripts
    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      const newIds: string[] = [];

      schemas.forEach((schema, index) => {
        const scriptId = `jsonld-${index}-${Date.now()}`;
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = scriptId;
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
        newIds.push(scriptId);
      });

      jsonLdIds.current = newIds;
    }

    return () => {
      jsonLdIds.current.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}